import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Rating,
  Skeleton,
  Tab,
  TextField,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import BlockIcon from '@mui/icons-material/DoNotDisturbAltOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VerifiedIcon from '@mui/icons-material/VerifiedOutlined';
import { apiClient } from '../../apiClient';
import { API_ENDPOINTS } from '../../constants';
import { Review } from '../../types';
import { extractApiErrorMessage } from '../../utils/errorHandler';
import { resolveImageUrl } from '../../utils/image';

const TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'all', label: 'All' }
];

const AdminReviewsPage: React.FC = () => {
  const [status, setStatus] = useState('pending');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [replyingId, setReplyingId] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');

  const saveReply = async (id: string) => {
    setBusyId(id);
    try {
      const response = await apiClient.patch(`${API_ENDPOINTS.REVIEWS}/${id}/reply`, {
        text: replyDraft
      });
      setReviews((prev) => prev.map((r) => (r._id === id ? (response.data as Review) : r)));
      setReplyingId(null);
      setReplyDraft('');
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to save reply'));
    } finally {
      setBusyId(null);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.REVIEWS}?status=${status}`);
      setReviews(response.data as Review[]);
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to load reviews'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load]);

  const moderate = async (id: string, next: 'approved' | 'rejected') => {
    setBusyId(id);
    try {
      await apiClient.patch(`${API_ENDPOINTS.REVIEWS}/${id}`, { status: next });
      setReviews((prev) => prev.filter((r) => (status === 'all' ? false : r._id !== id)));
      if (status === 'all') load();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to update review'));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this review permanently?')) return;
    setBusyId(id);
    try {
      await apiClient.delete(`${API_ENDPOINTS.REVIEWS}/${id}`);
      setReviews((prev) => prev.filter((r) => r._id !== id));
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to delete review'));
    } finally {
      setBusyId(null);
    }
  };

  const productOf = (review: Review) =>
    typeof review.product === 'object' && review.product ? review.product : null;

  return (
    <Box sx={{ maxWidth: 900 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Reviews
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Customer reviews stay hidden until you approve them
        </Typography>
      </Box>

      <Tabs value={status} onChange={(_, v) => setStatus(v)} sx={{ mb: 2 }}>
        {TABS.map((tab) => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        [...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={120} sx={{ mb: 2 }} />
        ))
      ) : reviews.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 5, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {status === 'pending'
              ? 'No reviews waiting for moderation. ✨'
              : `No ${status === 'all' ? '' : status} reviews yet.`}
          </Typography>
        </Paper>
      ) : (
        reviews.map((review) => {
          const product = productOf(review);
          return (
            <Paper key={review._id} variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                {product?.images?.[0] && (
                  <Box
                    component="img"
                    src={resolveImageUrl(product.images[0])}
                    alt=""
                    sx={{ width: 56, height: 56, borderRadius: 1.5, objectFit: 'cover', flexShrink: 0 }}
                  />
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {product?.name || 'Product removed'}
                    </Typography>
                    <Chip
                      size="small"
                      label={review.status}
                      color={
                        review.status === 'approved'
                          ? 'success'
                          : review.status === 'rejected'
                            ? 'error'
                            : 'warning'
                      }
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
                    <Rating value={review.rating} readOnly size="small" />
                    <Typography variant="caption" color="text.secondary">
                      {review.customerName} · {new Date(review.createdAt).toLocaleDateString()}
                    </Typography>
                    {review.verifiedPurchase && (
                      <Chip
                        size="small"
                        color="success"
                        variant="outlined"
                        icon={<VerifiedIcon sx={{ fontSize: 13 }} />}
                        label="Verified"
                        sx={{ height: 20 }}
                      />
                    )}
                  </Box>
                  {review.title && (
                    <Typography variant="subtitle2" sx={{ mb: 0.25 }}>
                      {review.title}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {review.comment}
                  </Typography>
                  {(review.images?.length || 0) > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                      {review.images!.map((image) => (
                        <Box
                          key={image}
                          component="a"
                          href={resolveImageUrl(image)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Box
                            component="img"
                            src={resolveImageUrl(image)}
                            alt="Customer photo"
                            sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1, border: '1px solid rgba(0,0,0,0.1)' }}
                          />
                        </Box>
                      ))}
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {(review.helpfulCount || 0) > 0 && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={`${review.helpfulCount} found helpful`}
                        sx={{ height: 20 }}
                      />
                    )}
                    {review.incentiveCouponCode && (
                      <Tooltip title="Thank-you coupon emailed to the customer on approval">
                        <Chip
                          size="small"
                          color="secondary"
                          variant="outlined"
                          label={`Reward sent: ${review.incentiveCouponCode}`}
                          sx={{ height: 20 }}
                        />
                      </Tooltip>
                    )}
                  </Box>

                  {replyingId === review._id ? (
                    <Box sx={{ mt: 1.5 }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        size="small"
                        label="Public reply from Vediluxe"
                        value={replyDraft}
                        onChange={(e) => setReplyDraft(e.target.value)}
                        sx={{ mb: 1 }}
                      />
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="contained"
                          disabled={busyId === review._id}
                          onClick={() => saveReply(review._id)}
                        >
                          {review.reply?.text && !replyDraft.trim() ? 'Remove reply' : 'Save reply'}
                        </Button>
                        <Button size="small" onClick={() => setReplyingId(null)}>
                          Cancel
                        </Button>
                      </Box>
                    </Box>
                  ) : review.reply?.text ? (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 1.25,
                        borderLeft: '3px solid',
                        borderColor: 'primary.main',
                        backgroundColor: '#FCF6F9',
                        borderRadius: '0 8px 8px 0'
                      }}
                    >
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        Your reply · {new Date(review.reply.repliedAt).toLocaleDateString()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {review.reply.text}
                      </Typography>
                      <Button
                        size="small"
                        sx={{ mt: 0.5 }}
                        onClick={() => {
                          setReplyingId(review._id);
                          setReplyDraft(review.reply?.text || '');
                        }}
                      >
                        Edit reply
                      </Button>
                    </Box>
                  ) : (
                    <Button
                      size="small"
                      sx={{ mt: 1 }}
                      onClick={() => {
                        setReplyingId(review._id);
                        setReplyDraft('');
                      }}
                    >
                      Reply publicly
                    </Button>
                  )}
                </Box>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flexShrink: 0 }}>
                  {review.status !== 'approved' && (
                    <Tooltip title="Approve — publishes on the product page">
                      <span>
                        <Button
                          size="small"
                          color="success"
                          startIcon={<CheckIcon fontSize="small" />}
                          disabled={busyId === review._id}
                          onClick={() => moderate(review._id, 'approved')}
                        >
                          Approve
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  {review.status !== 'rejected' && (
                    <Tooltip title="Reject — keeps it hidden">
                      <span>
                        <Button
                          size="small"
                          color="inherit"
                          startIcon={<BlockIcon fontSize="small" />}
                          disabled={busyId === review._id}
                          onClick={() => moderate(review._id, 'rejected')}
                        >
                          Reject
                        </Button>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title="Delete permanently">
                    <span>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={busyId === review._id}
                        onClick={() => remove(review._id)}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Box>
            </Paper>
          );
        })
      )}
    </Box>
  );
};

export default AdminReviewsPage;
